import { Brand } from '@/api/type';
import { useMenuProvide } from '@/hooks/auth/index';
import { AuthItem, Tool } from '@/hooks/auth/menuType';
import { router } from '@/router/index';
import { ResCurrentUser } from '@/type/auth';
import { MenuItem } from '@/type/menu';
import { defineStore } from 'pinia';
import { RouteRecordRaw } from 'vue-router';

// 公共信息
interface CommonState {
  hasLogin: boolean; // 登录状态
  userInfo: ResCurrentUser; // 用户信息
  menuList: Array<MenuItem>; // 菜单列表
  curAuth: AuthItem; // 当前页面权限
  allAuth: AuthItem[]; // 所有权限
  brand: any; // 品牌100 希望学 120 希望素养
}

interface Option {
  childMenu?: AuthItem[];
  type?: number;
}

export const ToolType = {
  SINGLE: 'single_tool',
  BATCH: 'batch_tool'
};

export default defineStore('common', {
  state(): CommonState {
    return {
      hasLogin: false,
      userInfo: {
        account: '',
        email: '',
        name: '',
        teacherId: 0,
        workcode: 0,
        account_name: ''
      },
      menuList: [],
      curAuth: {} as AuthItem,
      allAuth: [],
      brand: Brand.xwx
    };
  },

  getters: {
    /**
     * 获取模块信息
     * @param {object} option 参数
     * @param {Array} option.childMenu 子菜单（不传时，默认取当前页面的模块信息）
     * @param {number} option.type 班级类型（长短班）（不传时，默认返回所有班级模块信息）
     * @return {Array} 模块列表
     */
    getAuth: (state) => {
      return (pramas: Option) => {
        const { childMenu, type = 0 } = pramas;

        const authList: AuthItem[] = [];
        const auth = childMenu || state.curAuth?.child_menu || [];

        auth.forEach((item: AuthItem) => {
          // type为3时是模块
          if (item.type === 3) {
            // 不区分长短班
            if (!type) {
              authList.push(item);
            } else if (item.class_type === type) {
              // 区分长短班
              // 区分班级类型
              authList.push(item);
            }
          }
        });

        authList.sort((a, b) => a.sort - b.sort);
        return authList;
      };
    },

    /**
     * 获取当前模块下工具权限列表
     * @param {object} model 模块信息（不传时，默认取当前页面的模块信息）
     * @param {string} type 工具类型（不传时，默认所有工具信息，返回对象）
     * @return {object | Array} 工具信息
     */
    getTools: (state) => {
      return (model: AuthItem, type?: string) => {
        const toolList: AuthItem = model || state.curAuth;
        let result = null;
        switch (type) {
          case ToolType.SINGLE:
            result = toolList?.menu_info?.single_tool || [];
            break;
          case ToolType.BATCH:
            result =
              toolList?.menu_info?.batch_tool?.map((item: Tool) => {
                return { id: item.action, text: item.name, ...item };
              }) || [];
            break;
          default:
            result = {
              single_tool: toolList?.menu_info?.single_tool || [],
              batch_tool: toolList?.menu_info?.batch_tool || []
            };
        }
        return result;
      };
    },

    /**
     * 获取工具模版列表
     * @param {Array} tools 工具信息
     * @param {String} type 工具类型
     * @return {Array} 工具模版列表
     */
    getToolTemplateIds: () => {
      return (tools: Tool[], type: string) => {
        let tool: Tool = {};
        tools.forEach((item) => {
          if (item.action === type) {
            tool = item;
          }
        });
        const template_ids = tool?.template_ids || '';
        const template = template_ids
          ? template_ids.split(',').map((item) => {
              return Number(item);
            })
          : [];
        return template;
      };
    }
  },

  actions: {
    // 获取菜单权限
    async getMenuList() {
      const getAuthList = await useMenuProvide();
      const { menuList, routerList, originAuthList } = getAuthList;
      this.menuList = menuList;
      this.allAuth = originAuthList;
      routerList.forEach((item: RouteRecordRaw) => {
        router.addRoute(item);
      });
    },
    // 获取当前角色ID
    // 设置当前页面模块权限
    setCurAuth(path: string[]) {
      let authList = this.allAuth;
      let curAuth = {} as AuthItem;
      path.forEach((item) => {
        curAuth = getSingleAuth(item, authList);
        authList = curAuth?.child_menu || [];
      });

      function getSingleAuth(path: string, parentAuthList: AuthItem[]) {
        const authList = parentAuthList.filter((item, index) => {
          return item.url === path;
        });

        return authList[0];
      }
      this.curAuth = curAuth;
    }
  }
});
